// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import React from 'react';
import { Button, Stack, Alert, Box } from '@mui/material';
import { Link, useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import CustomTextField from '@/components/forms/theme-elements/CustomTextField';
import CustomFormLabel from '@/components/forms/theme-elements/CustomFormLabel';
import { apiService } from '@/services/api';

type ForgotPasswordFormValues = {
  email: string;
};

const schema = yup.object({
  email: yup.string().required('Email is required').email('Please provide a valid email address'),
});

const AuthForgotPassword = () => {
  const navigate = useNavigate();
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormValues>({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
    defaultValues: { email: '' },
  });

  const onSubmit = async (values: ForgotPasswordFormValues) => {
    setSubmitError(null);
    setIsSubmitting(true);
    
    try {
      await apiService.requestPasswordRecovery(values.email);
      // Navigate to verification page with recovery parameter and email
      navigate(`/verification?recovery=true&email=${encodeURIComponent(values.email)}`, { replace: true });
    } catch (err: any) {
      console.error('Password recovery request error:', err);
      setSubmitError(err?.message || 'Error sending recovery request, try again');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Stack mt={4} spacing={2}>
        {submitError ? (
          <Alert variant="filled" severity="error">{submitError}</Alert>
        ) : null}
        
        <CustomFormLabel htmlFor="reset-email">Email Address</CustomFormLabel>
        <CustomTextField 
          id="reset-email" 
          variant="outlined" 
          fullWidth
          error={!!errors.email}
          helperText={errors.email?.message}
          {...register('email')}
        />

        <Button 
          color="primary" 
          variant="contained" 
          size="large" 
          fullWidth 
          type="submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Sending...' : 'Send Recovery Email'}
        </Button>
        
        <Button color="primary" size="large" fullWidth component={Link} to="/">
          Back to Login
        </Button>
      </Stack>
    </Box>
  );
};

export default AuthForgotPassword;
